import { useEffect } from "react";
import { Link } from "react-router-dom";
import { applyPageSeo } from "../lib/seo";

export function About() {
  useEffect(() => {
    return applyPageSeo({
      title: "About",
      description:
        "Learn about React Email Builder — the AI-powered email template builder for developers. Built by Subcode Ventures in Cornwall, UK.",
      path: "/about",
      keywords: ["about react email builder", "subcode ventures", "email template builder team"],
    });
  }, []);

  return (
    <div className="bg-white text-slate-950">
      <section className="border-b border-slate-200 bg-gradient-to-b from-slate-50 to-white py-14 sm:py-18">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <p className="text-sm font-semibold uppercase tracking-[0.24em] text-sky-700">About</p>
          <h1 className="mt-4 text-4xl font-bold tracking-tight sm:text-5xl">
            Built for developers who ship emails.
          </h1>
          <p className="mt-4 text-lg leading-8 text-slate-600">
            Describe. Generate. Ship.
          </p>
        </div>
      </section>

      <section className="py-14 sm:py-16">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div className="space-y-12">
            <div>
              <h2 className="text-2xl font-bold text-slate-950">The problem</h2>
              <p className="mt-3 text-base leading-7 text-slate-600">
                Drag-and-drop builders produce unmaintainable HTML. React Email fixes that. We make it fast: describe what you need, generate a template, export and ship.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-950">What we believe</h2>
              <div className="mt-6 space-y-6">
                <div className="rounded border border-slate-200 bg-slate-50 px-4 py-3">
                  <h3 className="font-semibold text-slate-950">📧 Emails are code</h3>
                  <p className="mt-1 text-sm leading-6 text-slate-600">
                    React components in your repo, review in PRs, deploy with CI.
                  </p>
                </div>
                <div className="rounded border border-slate-200 bg-slate-50 px-4 py-3">
                  <h3 className="font-semibold text-slate-950">⚡ AI does the heavy lifting</h3>
                  <p className="mt-1 text-sm leading-6 text-slate-600">
                    Describe, generate, tweak, ship. Minutes, not days.
                  </p>
                </div>
                <div className="rounded border border-slate-200 bg-slate-50 px-4 py-3">
                  <h3 className="font-semibold text-slate-950">🔓 No lock-in</h3>
                  <p className="mt-1 text-sm leading-6 text-slate-600">
                    Export to clean HTML or React source. Any ESP. Modify the code. It's yours.
                  </p>
                </div>
                <div className="rounded border border-slate-200 bg-slate-50 px-4 py-3">
                  <h3 className="font-semibold text-slate-950">🆓 Free means free</h3>
                  <p className="mt-1 text-sm leading-6 text-slate-600">
                    No card required. 3 gen/day, 5 exports/mo, 10 drafts. Pro plan coming soon for higher limits.
                  </p>
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-950">Built by Subcode Ventures</h2>
              <p className="mt-2 text-base leading-7 text-slate-600">
                Cornwall, UK. We build developer tools that make complex workflows simple.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-950">The tech</h2>
              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                <div className="rounded border border-slate-200 bg-white p-3">
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Frontend</p>
                  <p className="mt-1.5 text-base font-semibold text-slate-950">React + Vite</p>
                </div>
                <div className="rounded border border-slate-200 bg-white p-3">
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Backend</p>
                  <p className="mt-1.5 text-base font-semibold text-slate-950">Cloudflare Workers + D1</p>
                </div>
                <div className="rounded border border-slate-200 bg-white p-3">
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">AI</p>
                  <p className="mt-1.5 text-base font-semibold text-slate-950">Google Gemini</p>
                </div>
                <div className="rounded border border-slate-200 bg-white p-3">
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Email rendering</p>
                  <p className="mt-1.5 text-base font-semibold text-slate-950">React Email + Sucrase</p>
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-950">Get in touch</h2>
              <div className="mt-4 flex flex-wrap gap-3">
                <a
                  href="mailto:hello@reactemailbuilder.com"
                  className="inline-flex items-center gap-2 rounded bg-slate-950 px-6 py-3 text-sm font-semibold text-white transition hover:bg-slate-800"
                >
                  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="h-4 w-4"><path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  Email us
                </a>
                <Link
                  to="/discover/templates"
                  className="inline-flex items-center gap-2 rounded border border-slate-300 bg-white px-6 py-3 text-sm font-semibold text-slate-700 transition hover:border-slate-400 hover:bg-slate-50"
                >
                  Browse templates
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}